import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { db } from "../firebase"; // adjust the path as needed
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";

const DailyToDo = () => {
  const [tasks, setTasks] = useState([]);
  const [newTaskName, setNewTaskName] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [userId, setUserId] = useState(null);
  const [showDeleteForTaskId, setShowDeleteForTaskId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (!userId) return;
    const fetchTasks = async () => {
      const q = query(
        collection(db, "dailyTasks"),
        where("userId", "==", userId)
      );
      const querySnapshot = await getDocs(q);
      const tasksData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTasks(tasksData);
    };
    fetchTasks();
  }, [userId]);

  const deleteTask = async (taskId) => {
    try {
      await setDoc(doc(db, "dailyTasks", taskId), {}, { merge: false });
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getDateKey = (date) => {
    return date.toISOString().split("T")[0];
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim() || !userId) return;

    const id = Date.now().toString();
    const newTask = {
      id,
      name: newTaskName,
      priority,
      completed: false,
      date: getDateKey(currentDate),
      userId,
    };

    await setDoc(doc(db, "dailyTasks", id), newTask);
    setTasks((prev) => [...prev, newTask]);
    setNewTaskName("");
    setPriority("Medium");
  };

  const toggleTaskCompletion = async (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updated = { ...task, completed: !task.completed };
    await updateDoc(doc(db, "dailyTasks", taskId), {
      completed: updated.completed,
    });

    setTasks((prev) =>
      prev.map((task) => (task.id === taskId ? updated : task))
    );
  };

  const handleInteractionStart = (taskId) => {
    if (isMobile) {
      // Start long-press timer
      const timer = setTimeout(() => {
        setShowDeleteForTaskId(taskId);
      }, 600); // long press = 600ms

      taskPressTimers[taskId] = timer;
    }
  };

  const handleInteractionEnd = (taskId) => {
    if (isMobile && taskPressTimers[taskId]) {
      clearTimeout(taskPressTimers[taskId]);
    }
  };

  const handleDoubleClick = (taskId) => {
    if (!isMobile) {
      setShowDeleteForTaskId(taskId);
    }
  };

  const taskPressTimers = {};

  const filteredTasks = tasks.filter(
    (task) => task.date === getDateKey(currentDate)
  );

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const order = { High: 1, Medium: 2, Low: 3 };
    return order[a.priority] - order[b.priority];
  });

  const completedTasks = sortedTasks.filter((task) => task.completed);
  const activeTasks = sortedTasks.filter((task) => !task.completed);

  return (
    <Container>
      <h2>Daily Tasks</h2>
      <DateSlider>
        <Arrow
          onClick={() =>
            setCurrentDate((d) => new Date(d.getTime() - 86400000))
          }
        >
          <FaChevronLeft />
        </Arrow>
        <CurrentDate>{formatDate(currentDate)}</CurrentDate>
        <Arrow
          onClick={() =>
            setCurrentDate((d) => new Date(d.getTime() + 86400000))
          }
        >
          <FaChevronRight />
        </Arrow>
      </DateSlider>
      <TaskList>
        {activeTasks.map((task) => (
          <Task
            key={task.id}
            completed={task.completed}
            onTouchStart={() => handleInteractionStart(task.id)}
            onTouchEnd={() => handleInteractionEnd(task.id)}
            onDoubleClick={() => handleDoubleClick(task.id)}
          >
            <TaskDetails>
              <TaskName completed={task.completed}>{task.name}</TaskName>
              <PriorityLabel>{task.priority}</PriorityLabel>
            </TaskDetails>
            <div style={{ display: "flex", gap: "10px" }}>
              {showDeleteForTaskId === task.id && (
                <Button
                  onClick={() => deleteTask(task.id)}
                  style={{ background: "#ff4d4f", color: "white" }}
                >
                  Delete
                </Button>
              )}
              <Button onClick={() => toggleTaskCompletion(task.id)}>
                {task.completed ? "Undo" : "Complete"}
              </Button>
            </div>
          </Task>
        ))}
        {completedTasks.map((task) => (
          <Task key={task.id} completed={task.completed}>
            <TaskDetails>
              <TaskName completed={task.completed}>{task.name}</TaskName>
              <PriorityLabel>{task.priority}</PriorityLabel>
            </TaskDetails>
            <Button onClick={() => toggleTaskCompletion(task.id)}>Undo</Button>
          </Task>
        ))}
      </TaskList>
      <AddTaskForm onSubmit={addTask}>
        <TaskInput
          type="text"
          placeholder="Add a task..."
          value={newTaskName}
          onChange={(e) => setNewTaskName(e.target.value)}
        />
        <SelectPriority
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </SelectPriority>
        <AddButton type="submit">Add</AddButton>
      </AddTaskForm>
    </Container>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 94%;
  margin: 30px 15px 30px 15px;
  padding: 10px;
  background-color: #171c29;
  border-radius: 20px;
  font-family: "Comfortaa";
  h2 {
    margin-top: 1em;
    margin-bottom: 1em;
    text-align: center;
  }
`;

const DateSlider = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const Arrow = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
`;

const CurrentDate = styled.div`
  color: #ffffff;
  font-size: 18px;
`;

const TaskList = styled.div`
  margin-bottom: 20px;
  max-height: 310px;
  overflow-y: scroll;
`;

const Task = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #20273a;
  border-radius: 15px;
  margin-bottom: 15px;
  animation: ${fadeIn} 0.3s ease;
`;

const TaskDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const TaskName = styled.span`
  font-size: 16px;
  color: ${(props) => (props.completed ? "#5a5a5a" : "#ffffff")};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
`;

const PriorityLabel = styled.span`
  font-size: 12px;
  color: #d6d6d6;
`;

const Button = styled.button`
  padding: 8px 10px;
  font-size: 14px;
  background: #ffffff;
  color: #303030;
  border: none;
  border-radius: 10px;
  cursor: pointer;
`;

const AddTaskForm = styled.form`
  display: flex;
  margin-top: 20px;
`;

const TaskInput = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 14px;
  border-radius: 15px 0 0 15px;
  border: none;
  background-color: #3b445c;
  color: white;
  outline: none;
`;

const SelectPriority = styled.select`
  flex: 1;
  padding: 15px;
  font-size: 14px;
  background-color: #3b445c;
  color: white;
  border: none;
  outline: none;
`;

const AddButton = styled.button`
  padding: 10px 15px;
  font-size: 14px;
  background: #e7e7e7;
  color: #252525;
  border: none;
  border-radius: 0 15px 15px 0;
  cursor: pointer;
`;

export default DailyToDo;
